import {STALE_TIME} from '@/utils/reactQueryClient';
import {
  CampaignStatsMetadata,
  CampaignStatsMetadataSchema,
  SocialStreamTab,
} from '@/types/socialMediaPost';
import campaignSocialMediaPostsFetchers from '@/api/fetchers/campaignSocialMediaPosts';
import useDataQuery from '@/hooks/use-data-query';

export const QUERY_KEY = 'campaignMetrics';

interface UseCampaignMetricsQueryParams {
  campaignId: string;
  socialStream?: SocialStreamTab;
  enabled?: boolean;
  startDate?: string;
  endDate?: string;
}

interface UseCampaignMetricsQueryResult {
  campaignStats: CampaignStatsMetadata | undefined;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
  error: Error | null;
}

function useCampaignMetricsQuery({
  campaignId,
  socialStream = 'feed',
  enabled = true,
  startDate,
  endDate,
}: UseCampaignMetricsQueryParams): UseCampaignMetricsQueryResult {
  const queryResult = useDataQuery({
    queryKey: [QUERY_KEY, campaignId, socialStream, startDate, endDate],
    queryFn: () =>
      campaignSocialMediaPostsFetchers.fetchMetrics({
        campaignId,
        socialStream,
        startDate,
        endDate,
      }),
    enabled: enabled && !!campaignId,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
  });

  // Validate data with Zod schema for type safety
  const parseResult = queryResult.data
    ? CampaignStatsMetadataSchema.safeParse(queryResult.data)
    : null;
  const campaignStats: CampaignStatsMetadata | undefined = parseResult?.success
    ? parseResult.data
    : undefined;

  return {
    campaignStats,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    refetch: queryResult.refetch,
    error: queryResult.error as Error | null,
  };
}

export default useCampaignMetricsQuery;
